// Medical Portal — Report Queries (list screen)
//
// Data source: Trust OPM, through our backend
//   GET /api/queries/complaints?clientId=…&q=…&status=…&page=…&per_page=…
// Every query raised here is a customer complaint in OPM. OPM is where
// the CSD assigns the department, where the TrustTicket and QTQMS
// status come back to and where the department's replies land, so this
// list shows exactly what OPM shows for the centre.
//
// Search, the status filter and paging run on the server (OPM), the same
// way the website's Support page works: the search matches the complaint
// number, the query text, visit id, UHID, ticket code and department.
//
// Layout:
//   · Filter row — search + status + page size
//   · Status tabs — All / Received / In progress / Resolved / Closed with counts
//   · Card list — icon · content · right-aligned status/date
//   · Pagination
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import { MP, MONO, StatusBadge, SectionCard, CardHeader } from './shared';
import ComplaintDetailModal from './ComplaintDetailModal';
import { useAuth } from '../../../context/AuthContext';
import { useListPortalComplaintsQuery } from '../../../store/queriesApi';

// Tab key = the `status` the backend understands.
const STATUS_TABS = [
  { key: '',            label: 'All' },
  { key: 'received',    label: 'Received' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved',    label: 'Resolved' },
  { key: 'closed',      label: 'Closed' },
];
const COUNT_KEY = { '': 'all', received: 'received', in_progress: 'in_progress', resolved: 'resolved', closed: 'closed' };
const PAGE_SIZES = [10, 20, 50];

// OPM status → visual treatment on the card (icon bg + stripe color).
const statusVisual = (status = '') => {
  if (status === 'resolved') return { bg: MP.greenLight, icon: '✅', stripe: MP.green };
  if (status === 'assigned' || status === 'in_progress') return { bg: MP.orangeLight, icon: '🔍', stripe: MP.yellow };
  if (status === 'closed') return { bg: MP.bg, icon: '⟐', stripe: MP.textMuted };
  return { bg: MP.redLight, icon: '⚠', stripe: MP.red };   // open → new
};

// "2026-10-26 14:02:11" / ISO → "26 Oct 2026"
const formatDate = (raw) => {
  if (!raw) return '—';
  const d = new Date(String(raw).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ReportQueries = ({ onOpenComplaint }) => {
  const { user } = useAuth();
  const clientId = user?.client_id;

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');            // debounced search sent to the server
  const [status, setStatus] = useState('');  // tab / dropdown — one value
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [openRecord, setOpenRecord] = useState(null);

  // Typing → one request 350 ms after the last key, back to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching, isError, error, refetch } = useListPortalComplaintsQuery(
    { clientId, q, status, page, per_page: perPage },
    { skip: !clientId },
  );

  // The list keeps itself current without a Refresh button.
  //
  // The backend answers instantly from its cache, and when that copy was
  // stale it starts a refresh behind the response and says so with
  // `_stale`. We come back once, a couple of seconds later, to collect the
  // newer list — so the page paints immediately AND lands on fresh data on
  // its own.
  //
  // This is NOT polling: it fires only when the server said a refresh was
  // already running, and the follow-up answer is never itself stale (the
  // refresh has landed by then), so it cannot loop. An idle page makes no
  // requests at all.
  useEffect(() => {
    if (!data?._stale) return undefined;
    const id = setTimeout(() => { refetch(); }, 2500);
    return () => clearTimeout(id);
  }, [data, refetch]);

  const records = useMemo(() => data?.records || [], [data]);
  const counts = data?.counts || {};
  const pagination = data?.pagination || { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 };

  const pickStatus = (key) => { setStatus(key); setPage(1); };
  const pickPerPage = (n) => { setPerPage(n); setPage(1); };

  const openCard = (r) => {
    setOpenRecord(r);
    onOpenComplaint?.(r.no);
  };

  const showSkeletons = isLoading && records.length === 0;
  const busy = isFetching && !isLoading;

  return (
    <Box>
      {/* ── Filter row ── */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: MP.radius,
          boxShadow: MP.shadow,
          p: '13px',
          mb: 1.75,
        }}
      >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search by complaint no, query text, visit, UHID, ticket or department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ...filterInputSx, flex: { md: 1 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MP.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select size="small"
          label="Status"
          value={status}
          onChange={(e) => pickStatus(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ ...filterInputSx, minWidth: 180 }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="received">Received</MenuItem>
          <MenuItem value="in_progress">In progress</MenuItem>
          <MenuItem value="resolved">Resolved</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
        <TextField
          select size="small"
          label="Per page"
          value={perPage}
          onChange={(e) => pickPerPage(Number(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ ...filterInputSx, minWidth: 110 }}
        >
          {PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </TextField>
      </Stack>
      </Box>

      {/* ── Status tabs ── */}
      <Box
        sx={{
          borderBottom: `1px solid ${MP.border}`,
          mb: 2,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: 0 },
        }}
      >
        <Stack direction="row" spacing={0}>
          {STATUS_TABS.map(({ key, label }) => {
            const isActive = status === key;
            const count = counts[COUNT_KEY[key]] || 0;
            return (
              <Box
                key={key || 'all'}
                onClick={() => pickStatus(key)}
                sx={{
                  px: '14px', py: '9px',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: isActive ? MP.teal : MP.textMuted,
                  borderBottom: `2px solid ${isActive ? MP.teal : 'transparent'}`,
                  mb: '-1px',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  whiteSpace: 'nowrap',
                  transition: 'color .15s, border-color .15s',
                  '&:hover': isActive ? {} : { color: MP.dark },
                }}
              >
                {label}
                {count > 0 && (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: MP.borderSoft,
                      color: MP.ink3,
                      fontFamily: MONO, fontSize: 10.5,
                      minWidth: 20, textAlign: 'center',
                      px: '5px', py: '1px', borderRadius: '3px',
                    }}
                  >
                    {count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── Query register ── */}
      <SectionCard>
      <CardHeader
        title="Report Queries"
        subtitle={
          pagination.total
            ? `Showing ${pagination.from}–${pagination.to} of ${pagination.total} · from Trust OPM${busy ? ' · updating…' : ''}`
            : `No queries · from Trust OPM${busy ? ' · updating…' : ''}`
        }
      />
      <Stack spacing={1.25} sx={{ p: '15px', opacity: busy ? 0.7 : 1, transition: 'opacity .15s' }}>
        {showSkeletons && Array.from({ length: 5 }).map((_, i) => (
          <QueryItemSkeleton key={`sk-${i}`} />
        ))}

        {!showSkeletons && records.map((r) => (
          <QueryItem key={r.id} r={r} onClick={() => openCard(r)} />
        ))}

        {!showSkeletons && records.length === 0 && (
          <Box
            sx={{
              py: 6, textAlign: 'center', color: MP.textMuted,
              bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: '6px',
              boxShadow: MP.shadow,
              fontSize: 13,
            }}
          >
            {!clientId
              ? 'Your account is missing a Client ID — contact admin to link one.'
              : isError
                ? (error?.data?.message || 'Could not load your queries from the complaint service.')
                : (q || status)
                  ? 'No queries match the search or filter.'
                  : 'No queries raised yet. Click "New Query" to get started.'}
          </Box>
        )}

        {pagination.last_page > 1 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={1} sx={{ pt: 1 }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted }}>
              Page {pagination.current_page} of {pagination.last_page}
            </Typography>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={(_, p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              size="small"
              shape="rounded"
              siblingCount={1}
              sx={{
                '& .MuiPaginationItem-root': { fontFamily: MONO, fontSize: 12 },
                '& .Mui-selected': { bgcolor: `${MP.tealLight} !important`, color: MP.tealDark, fontWeight: 700 },
              }}
            />
          </Stack>
        )}
      </Stack>
      </SectionCard>

      <ComplaintDetailModal
        open={!!openRecord}
        onClose={() => setOpenRecord(null)}
        record={openRecord}
      />
    </Box>
  );
};

// ── Single query card ─────────────────────────────────────────
const QueryItem = ({ r, onClick }) => {
  const visual = statusVisual(r.status);
  const isPatient = !!r.visit_id;

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: MP.white,
        border: `1px solid ${MP.border}`,
        borderRadius: '6px',
        boxShadow: MP.shadow,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all .18s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: 4,
          bgcolor: visual.stripe,
        },
        '&:hover': {
          borderColor: MP.teal,
          boxShadow: '0 2px 12px rgba(16,21,28,.10)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ px: 2.25, py: 2, pl: 2.75 }}
      >
        <Stack direction="row" spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: '6px',
              bgcolor: visual.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, flexShrink: 0,
            }}
          >
            {visual.icon}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Meta row — complaint no + query code + type pill */}
            <Stack
              direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}
              sx={{ mb: 0.5 }}
            >
              <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.tealDark, fontWeight: 700 }}>
                {r.no}
              </Typography>
              {r.query_code && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: MP.border }} />
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>
                    {r.query_code}
                  </Typography>
                </>
              )}
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: MP.border }} />
              <Box
                sx={{
                  bgcolor: isPatient ? MP.tealLight : MP.yellowLight,
                  color:   isPatient ? MP.tealDark : '#92600A',
                  fontFamily: MONO, fontSize: 10, fontWeight: 700,
                  px: '8px', py: '2px', borderRadius: '20px',
                }}
              >
                {isPatient ? 'PATIENT-SPECIFIC' : 'GENERAL SERVICE'}
              </Box>
            </Stack>

            {/* Description — clamped to 2 lines */}
            <Typography
              sx={{
                fontWeight: 600, fontSize: 14, color: MP.dark,
                mb: 0.75, lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {r.description || r.subject || '(No description)'}
            </Typography>

            {/* Sub-meta — visit / date / attachments / raised by / department / ticket / replies */}
            <Stack
              direction="row" flexWrap="wrap"
              spacing={0}
              rowGap={0.5}
              sx={{ fontSize: 12, color: MP.textMuted }}
            >
              {isPatient && <MetaChip icon="👥" text={`Visit: ${r.visit_id}`} />}
              <MetaChip icon="📅" text={formatDate(r.raised_at)} />
              {(r.attachments || []).length > 0 && (
                <MetaChip icon="📎" text={`${r.attachments.length} attachment${r.attachments.length === 1 ? '' : 's'}`} />
              )}
              {r.raised_by_user?.name && (
                <MetaChip
                  icon={r.raised_by_user.role === 'Doctor' ? '👨‍⚕️' : '🏥'}
                  text={`Raised by ${r.raised_by_user.name}`}
                />
              )}
              {r.info_request && !r.info_request.answered && r.status !== 'closed' && (
                <MetaChip icon="❗" text="Information needed from you" />
              )}
              {r.callback && (
                <MetaChip icon="📞" text={r.callback.called ? 'Doctor called' : 'Doctor will call you'} />
              )}
              {r.department && <MetaChip icon="🏢" text={r.department} />}
              {r.ticket_code && (
                <MetaChip icon="🎫" text={`${r.ticket_code}${r.ticket_status ? ` · ${r.ticket_status}` : ''}`} />
              )}
              {r.reply_count > 0 && (
                <MetaChip icon="💬" text={`${r.reply_count} repl${r.reply_count === 1 ? 'y' : 'ies'}`} />
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Right column — status */}
        <Stack
          direction={{ xs: 'row', sm: 'column' }}
          alignItems={{ xs: 'center', sm: 'flex-end' }}
          justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
          spacing={1}
          sx={{ flexShrink: 0, minWidth: { sm: 150 } }}
        >
          <StatusBadge>{r.status_label}</StatusBadge>
          <Typography
            sx={{
              fontFamily: MONO, fontSize: 11, color: MP.textMuted,
              textAlign: { xs: 'left', sm: 'right' }, whiteSpace: 'nowrap',
            }}
          >
            {formatDate(r.updated_at || r.raised_at)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

// Skeleton card — same shape as a real card so the grid doesn't jump.
const QueryItemSkeleton = () => (
  <Box
    sx={{
      bgcolor: MP.white,
      border: `1px solid ${MP.border}`,
      borderRadius: '6px',
      boxShadow: MP.shadow,
      px: 2.75, py: 2,
    }}
  >
    <Stack direction="row" spacing={1.75} alignItems="flex-start">
      <Skeleton variant="rounded" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Skeleton variant="text" width={80} height={14} />
          <Skeleton variant="text" width={100} height={14} />
        </Stack>
        <Skeleton variant="text" width="80%" height={18} />
        <Skeleton variant="text" width="50%" height={14} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={80} height={22} />
    </Stack>
  </Box>
);

// Dot-separated meta atom
const MetaChip = ({ icon, text }) => (
  <Stack
    direction="row" spacing={0.5} alignItems="center"
    sx={{
      mr: 2,
      '&:last-of-type': { mr: 0 },
    }}
  >
    <Box component="span" sx={{ fontSize: 12 }}>{icon}</Box>
    <Typography sx={{ fontSize: 12, color: MP.textMuted }}>{text}</Typography>
  </Stack>
);

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white,
    borderRadius: '6px',
    fontSize: 13,
    fontFamily: "'Outfit', sans-serif",
    '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: MP.teal },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1.5 },
  },
};

export default ReportQueries;
